import { useMemo, useEffect, useState } from 'react';
import { useProduct, ProductProvider } from '@workspace/utils-react';
import { AuthProvider } from 'react-oidc-context';
import { MemoryRouter, BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from '@tanstack/react-query';
import i18next from 'i18next';
import { initReactI18next, I18nextProvider } from 'react-i18next';
import { LanguageSwitcher, ShadowRootProvider, InstanceProvider, EvoIframeMfesProvider } from '@access-evo/react-app-api';
import App from './App';
import { LoginCallback } from './LoginCallback';
import { getOidcConfig } from './oidcConfig';
import { parseDeepLinkParams } from './hooks';
import resources from './resources.json';
import type { identityUrl, workspaceUrl } from '@workspace/utils';
import { isLocalDevelopment, isIframeMode, isStandaloneMode, isPreprodEnvironment } from './lib/environmentUtils';
import { ApiError } from './api/shared/apiError';
import { toast } from 'sonner';

function handleGlobalApiError(error: unknown): void {
    if (error instanceof ApiError && error.isUnauthorized) {
        toast.error('Unauthorized: You do not have permission to access this resource. Please check your credentials or contact your administrator.');
    }
}

export const queryClient = new QueryClient({
    queryCache: new QueryCache({
        onError: handleGlobalApiError,
    }),
    mutationCache: new MutationCache({
        onError: handleGlobalApiError,
    }),
});
async function createi18nInstance() {
    const mfeI18n = i18next.createInstance();
    await mfeI18n.use(initReactI18next).init({
        lng: 'en-GB',
        fallbackLng: 'en-GB',
        debug: false,
        interpolation: {
            escapeValue: false,
        },
        resources,
    });
    return mfeI18n;
}
export interface Settings {
    identityUrl: identityUrl;
    workspaceUrl: workspaceUrl;
    apiUrl: string;
    n8nUrl?: string;
}
const LoadingScreen = () => (
    <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto" />
            <p className="text-gray-600">Initializing...</p>
        </div>
    </div>
);

interface I18nWrapperProps {
    children: React.ReactNode;
}

const I18nWrapper = ({ children }: I18nWrapperProps) => {
    const [i18n, setI18n] = useState<typeof i18next | null>(null);

    useEffect(() => {
        createi18nInstance().then(setI18n);
    }, []);

    if (!i18n) {
        return <LoadingScreen />;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
                <LanguageSwitcher i18n={i18n}>
                    <ShadowRootProvider shadowRoot={null}>
                        <EvoIframeMfesProvider>
                            {children}
                        </EvoIframeMfesProvider>
                    </ShadowRootProvider>
                </LanguageSwitcher>
            </I18nextProvider>
        </QueryClientProvider>
    );
};

const getLocalDevProduct = () => ({
    settings: {
        apiUrl: import.meta.env.VITE_API_BASE_URL || `${window.location.origin}/api/v1`,
        n8nUrl: import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://n8n.dev.ax.accessacloud.com/webhook/ac9ab361-4c74-47ad-95d1-11ba589459e2',
    },
} as Parameters<typeof ProductProvider>[0]['iframePidget']);

interface RouterWrapperProps {
    children: React.ReactNode;
    initialRoute?: string;
}

const RouterWrapper = ({ children, initialRoute = '/' }: RouterWrapperProps) => {
    if (isIframeMode()) {
        return (
            <MemoryRouter initialEntries={[initialRoute]}>
                {children}
            </MemoryRouter>
        );
    }
    return (
        <BrowserRouter>
            {children}
        </BrowserRouter>
    );
};

export const AppContainer = () => {
    const product = useProduct();
    const oidcConfig = getOidcConfig();
    
    const deepLinkParams = useMemo(() => parseDeepLinkParams(), []);
    const initialRoute = deepLinkParams.route;

    if (isLocalDevelopment()) {
        console.log('ProjectApproval_ Development mode detected - bypassing OIDC authentication');
        console.log('ProjectApproval_ Deep link params:', deepLinkParams);
        const instanceId = new URLSearchParams(window.location.search).get('instanceId') || 'dev-instance';
        return (
            <I18nWrapper>
                <InstanceProvider instanceId={instanceId}>
                    <RouterWrapper initialRoute={initialRoute}>
                        <Routes>
                            <Route path="/login/callback" element={<LoginCallback />} />
                            <Route path="/*" element={
                                <ProductProvider iframePidget={product || getLocalDevProduct()}>
                                    <App />
                                </ProductProvider>
                            } />
                        </Routes>
                    </RouterWrapper>
                </InstanceProvider>
            </I18nWrapper>
        );
    }

    if (product?.settings?.identityUrl) {
        const mode = isPreprodEnvironment() ? 'Preprod' : 'Iframe';
        console.log(`ProjectApproval_ ${mode} mode with auth - initial route:`, initialRoute);
        const instanceId = new URLSearchParams(window.location.search).get('instanceId') || '';
        return (
            <I18nWrapper>
                <InstanceProvider instanceId={instanceId}>
                    <RouterWrapper initialRoute={initialRoute}>
                        <Routes>
                            <Route path="/login/callback" element={<LoginCallback />} />
                            <Route path="/*" element={
                                <ProductProvider iframePidget={product}>
                                    <AuthProvider
                                        authority={product.settings.identityUrl}
                                        {...oidcConfig}
                                    >
                                        <App />
                                    </AuthProvider>
                                </ProductProvider>
                            } />
                        </Routes>
                    </RouterWrapper>
                </InstanceProvider>
            </I18nWrapper>
        );
    }

    if (isStandaloneMode()) {
        console.log('ProjectApproval_ Standalone mode detected - running without Evo platform context');
        console.log('ProjectApproval_ Deep link params:', deepLinkParams);
        return (
            <I18nWrapper>
                <InstanceProvider instanceId="standalone">
                    <RouterWrapper initialRoute={initialRoute}>
                        <Routes>
                            <Route path="/login/callback" element={<LoginCallback />} />
                            <Route path="/*" element={
                                <ProductProvider iframePidget={product || getLocalDevProduct()}>
                                    <App />
                                </ProductProvider>
                            } />
                        </Routes>
                    </RouterWrapper>
                </InstanceProvider>
            </I18nWrapper>
        );
    }

    return <LoadingScreen />;
};
